import React, { useState } from "react";
import useSWR from "swr";
import { Link } from "react-router-dom";
import api, { money, fmtDate } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInputES } from "@/components/ui/DateInputES";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import { Plus, Copy, Pencil, Trash2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { TOUR_STATUS } from "@/lib/labels";

const fetcher = (url) => api.get(url).then((r) => r.data);

const empty = {
  name: "", destination: "", country: "", tour_type: "workshop",
  start_date: "", end_date: "", price_per_participant: 4000, currency: "USD",
  max_clients: 8, min_clients: 4, guides_count: 1, vehicles_count: 1,
  status: "confirmed", description: "", cover_image: "",
  guides: [],
  deposit_amount: 1000, final_due_days_before: 30,
};

export default function Tours() {
  // SWR con revalidateOnFocus:false → NO refetch cuando el teclado móvil abre/cierra el viewport.
  const { data, mutate } = useSWR("/tours", fetcher, { revalidateOnFocus: false });
  const { data: countByTour } = useSWR("/participant-counts", fetcher, { revalidateOnFocus: false });
  const { data: guidesList } = useSWR("/guides", fetcher, { revalidateOnFocus: false });
  // Persistimos estado del diálogo en sessionStorage: si el componente
  // se desmonta por cualquier motivo (hot reload, re-render, navegación
  // fantasma), al remontarse recuperamos exactamente donde estabas.
  const [open, setOpen] = useState(() => {
    try { return sessionStorage.getItem("tour-dlg-open") === "1"; } catch { return false; }
  });
  const [form, setForm] = useState(() => {
    try {
      const raw = sessionStorage.getItem("tour-dlg-form");
      return raw ? { ...empty, ...JSON.parse(raw) } : empty;
    } catch { return empty; }
  });
  const [editingId, setEditingId] = useState(() => {
    try { return sessionStorage.getItem("tour-dlg-editing") || null; } catch { return null; }
  });
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    try { sessionStorage.setItem("tour-dlg-open", open ? "1" : "0"); } catch {}
  }, [open]);
  // Debounce: solo escribimos el formulario a sessionStorage 400ms después
  // de dejar de teclear. Evita bloquear el main thread al escribir rápido
  // en móvil (iOS/Safari cerraba el tab por "página no responde").
  React.useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      try { sessionStorage.setItem("tour-dlg-form", JSON.stringify(form)); } catch {}
    }, 400);
    return () => clearTimeout(t);
  }, [form, open]);
  React.useEffect(() => {
    try {
      if (editingId) sessionStorage.setItem("tour-dlg-editing", editingId);
      else sessionStorage.removeItem("tour-dlg-editing");
    } catch {}
  }, [editingId]);

  const submit = async () => {
    setSaving(true);
    const payload = { ...form,
      price_per_participant: Number(form.price_per_participant),
      max_clients: Number(form.max_clients), min_clients: Number(form.min_clients),
      guides_count: Number(form.guides_count), vehicles_count: Number(form.vehicles_count),
      deposit_amount: Number(form.deposit_amount),
      final_due_days_before: Number(form.final_due_days_before) };
    try {
      if (editingId) {
        await api.patch(`/tours/${editingId}`, payload);
        toast.success("Tour actualizado");
      } else {
        await api.post("/tours", payload);
        toast.success("Tour creado");
      }
      setForm(empty); setEditingId(null); setOpen(false);
      try {
        sessionStorage.removeItem("tour-dlg-open");
        sessionStorage.removeItem("tour-dlg-form");
        sessionStorage.removeItem("tour-dlg-editing");
      } catch {}
      mutate();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const openEdit = (t) => {
    setForm({
      name: t.name || "", destination: t.destination || "", country: t.country || "",
      tour_type: t.tour_type || "workshop",
      start_date: t.start_date || "", end_date: t.end_date || "",
      price_per_participant: t.price_per_participant ?? 0, currency: t.currency || "USD",
      max_clients: t.max_clients ?? 8, min_clients: t.min_clients ?? 4,
      guides_count: t.guides_count ?? 1, vehicles_count: t.vehicles_count ?? 1,
      status: t.status || "confirmed", description: t.description || "", cover_image: t.cover_image || "",
      guides: t.guides || [],
      deposit_amount: t.deposit_amount ?? 1000,
      final_due_days_before: t.final_due_days_before ?? 30,
    });
    setEditingId(t.id); setOpen(true);
  };

  const closeDialog = (o) => {
    setOpen(o);
    if (!o) {
      setForm(empty); setEditingId(null);
      try {
        sessionStorage.removeItem("tour-dlg-open");
        sessionStorage.removeItem("tour-dlg-form");
        sessionStorage.removeItem("tour-dlg-editing");
      } catch {}
    }
  };

  const duplicate = async (id) => {
    await api.post(`/tours/${id}/duplicate`);
    toast.success("Tour duplicado");
    mutate();
  };

  const deleteTour = async (t) => {
    if (!window.confirm(`¿Eliminar la expedición "${t.name}"?\n\nSe archivará (se puede recuperar). Las reservas, pagos y gastos NO se borran.`)) return;
    try {
      await api.delete(`/tours/${t.id}`);
      toast.success("Expedición archivada");
      mutate();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo eliminar"); }
  };

  const handleCoverFile = (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) return toast.error("Solo se permiten imágenes");
    if (file.size > 5 * 1024 * 1024) return toast.error("Máximo 5 MB");
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const maxW = 1600;
        const scale = Math.min(1, maxW / img.width);
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        setForm((f) => ({ ...f, cover_image: dataUrl }));
        toast.success("Imagen preparada");
      };
      img.onerror = () => toast.error("No se pudo leer la imagen");
      img.src = reader.result;
    };
    reader.onerror = () => toast.error("No se pudo leer el archivo");
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Expediciones</h1>
          <p className="text-slate-500 mt-1">{data?.length || 0} tours registrados</p>
        </div>
        <Dialog open={open} onOpenChange={closeDialog}>
          <DialogTrigger asChild>
            <Button data-testid="new-tour-btn" className="bg-[#026FCB] hover:bg-[#0358a5]">
              <Plus size={16} className="mr-2" />Nuevo tour
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingId ? "Editar tour" : "Nuevo tour"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label>Nombre</Label><Input data-testid="tour-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></div>
              <div><Label>Destino</Label><Input data-testid="tour-destination" value={form.destination} onChange={(e) => setForm({...form, destination: e.target.value})} /></div>
              <div><Label>País</Label><Input data-testid="tour-country" value={form.country} onChange={(e) => setForm({...form, country: e.target.value})} /></div>
              <div><Label>Fecha inicio</Label><DateInputES data-testid="tour-start" value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})} /></div>
              <div><Label>Fecha fin</Label><DateInputES data-testid="tour-end" value={form.end_date} onChange={(e) => setForm({...form, end_date: e.target.value})} /></div>
              <div><Label>Precio por participante (USD)</Label><Input type="number" data-testid="tour-price" value={form.price_per_participant} onChange={(e) => setForm({...form, price_per_participant: e.target.value})} /></div>
              <div><Label>Depósito fijo (USD)</Label><Input type="number" data-testid="tour-deposit-amount" value={form.deposit_amount} onChange={(e) => setForm({...form, deposit_amount: e.target.value})} /><p className="text-[10px] text-slate-500 mt-1">Cantidad fija que el cliente paga como reserva. Por defecto 1000 US$.</p></div>
              <div><Label>Pago final vence (días antes)</Label><Input type="number" data-testid="tour-final-due-days" value={form.final_due_days_before} onChange={(e) => setForm({...form, final_due_days_before: e.target.value})} /><p className="text-[10px] text-slate-500 mt-1">Días antes del inicio del tour en que vence el saldo. Por defecto 30.</p></div>
              <div><Label>Máx. clientes</Label><Input type="number" data-testid="tour-max" value={form.max_clients} onChange={(e) => setForm({...form, max_clients: e.target.value})} /></div>
              <div><Label>Mín. clientes</Label><Input type="number" value={form.min_clients} onChange={(e) => setForm({...form, min_clients: e.target.value})} /></div>
              <div><Label>Guías</Label><Input type="number" value={form.guides_count} onChange={(e) => setForm({...form, guides_count: e.target.value})} /></div>
              <div><Label>Vehículos</Label><Input type="number" value={form.vehicles_count} onChange={(e) => setForm({...form, vehicles_count: e.target.value})} /></div>
              <div className="col-span-2">
                <Label>Tipo de tour</Label>
                <Select value={form.tour_type} onValueChange={(v) => setForm({...form, tour_type: v})}>
                  <SelectTrigger data-testid="tour-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="workshop">Workshop</SelectItem>
                    <SelectItem value="expedition">Expedición</SelectItem>
                    <SelectItem value="safari">Safari</SelectItem>
                    <SelectItem value="scouting">Scouting (reconocimiento)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-slate-500 mt-1">
                  Los <b>scouting</b> son viajes de exploración previa para valorar destinos, sin clientes.
                </p>
              </div>
              <div className="col-span-2">
                <Label>Estado</Label>
                <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                  <SelectTrigger data-testid="tour-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["confirmed","full","in_progress","finished","cancelled"].map((s) => (
                      <SelectItem key={s} value={s}>{TOUR_STATUS[s]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Imagen de portada</Label>
                <div className="flex items-start gap-4 mt-1">
                  <label
                    htmlFor="tour-cover-file"
                    className="flex-1 cursor-pointer border-2 border-dashed border-slate-300 dark:border-[#25334D] hover:border-[#026FCB] rounded-lg p-4 text-center transition-colors"
                    data-testid="tour-cover-drop"
                  >
                    <div className="text-sm font-medium text-[#026FCB]">Elegir imagen…</div>
                    <div className="text-xs text-slate-500 mt-1">JPG o PNG · máx 5 MB · se redimensiona a 1600 px</div>
                    <input
                      id="tour-cover-file"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      data-testid="tour-cover-file"
                      onChange={(e) => handleCoverFile(e.target.files?.[0])}
                    />
                  </label>
                  {form.cover_image && (
                    <div className="relative">
                      <img src={form.cover_image} alt="Portada" className="w-32 h-20 object-cover rounded-md border border-slate-200 dark:border-[#25334D]" />
                      <button
                        type="button"
                        onClick={() => setForm({...form, cover_image: ""})}
                        className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white dark:bg-[#131B2A] border border-slate-300 dark:border-[#25334D] text-slate-500 hover:text-red-500 text-xs"
                        title="Quitar imagen"
                      >×</button>
                    </div>
                  )}
                </div>
              </div>
              <div className="col-span-2"><Label>Descripción</Label><Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} /></div>
              <div className="col-span-2">
                <Label>Guías asignados</Label>
                <div className="mt-1 border border-slate-200 dark:border-[#25334D] rounded-lg p-2 max-h-40 overflow-y-auto space-y-1" data-testid="tour-guides-multi">
                  {(guidesList || []).filter((g) => g.active !== false).map((g) => {
                    const checked = (form.guides || []).includes(g.id);
                    return (
                      <label
                        key={g.id}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors ${
                          checked ? "bg-[#026FCB]/10" : "hover:bg-slate-50 dark:hover:bg-[#1A2336]"
                        }`}
                      >
                        <input
                          type="checkbox"
                          data-testid={`tour-guide-cb-${g.id}`}
                          checked={checked}
                          onChange={(e) => {
                            const set = new Set(form.guides || []);
                            if (e.target.checked) set.add(g.id); else set.delete(g.id);
                            setForm({ ...form, guides: Array.from(set) });
                          }}
                          className="accent-[#026FCB]"
                        />
                        <span className="text-sm truncate">{g.first_name} {g.last_name}</span>
                        {g.specialties && <span className="text-[10px] mono uppercase text-slate-400 ml-auto truncate max-w-[160px]">{g.specialties}</span>}
                      </label>
                    );
                  })}
                  {(!guidesList || guidesList.filter((g) => g.active !== false).length === 0) && (
                    <div className="text-xs text-slate-500 px-2 py-3">
                      No hay guías activos. Añádelos desde <b>Guías</b>.
                    </div>
                  )}
                </div>
                {(form.guides?.length || 0) > 0 && (
                  <div className="text-[11px] mono uppercase tracking-wider text-[#026FCB] mt-1">
                    {form.guides.length} guía(s) seleccionado(s)
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => closeDialog(false)}>Cancelar</Button>
              <Button data-testid="tour-save" className="bg-[#026FCB] hover:bg-[#0358a5]" onClick={submit} disabled={saving}>{saving ? "Guardando…" : (editingId ? "Guardar cambios" : "Guardar")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div>
        {(() => {
          const isScouting = (t) => (t.tour_type || "").toLowerCase() === "scouting";
          const todayISO = new Date().toISOString().slice(0, 10);
          // Upcoming first (asc), past at the end (desc within past)
          const byDate = (a, b) => {
            const da = a.start_date || "";
            const db = b.start_date || "";
            const aFuture = da >= todayISO;
            const bFuture = db >= todayISO;
            if (aFuture && !bFuture) return -1;
            if (!aFuture && bFuture) return 1;
            return aFuture ? da.localeCompare(db) : db.localeCompare(da);
          };
          const active = (data || []).filter((t) => !["finished","cancelled"].includes(t.status) && !isScouting(t)).sort(byDate);
          const scouting = (data || []).filter((t) => isScouting(t) && t.status !== "cancelled").sort(byDate);
          const finished = (data || []).filter((t) => t.status === "finished" && !isScouting(t)).sort((a,b) => (b.start_date || "").localeCompare(a.start_date || ""));
          const cancelled = (data || []).filter((t) => t.status === "cancelled").sort((a,b) => (b.start_date || "").localeCompare(a.start_date || ""));
          const Grid = ({ list, empty }) => (
            list.length === 0 ? (
              <Card><CardContent className="p-10 text-center text-slate-500">{empty}</CardContent></Card>
            ) : (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
                {list.map((t) => (
                  <Card key={t.id} className="overflow-hidden group hover:shadow-lg transition-shadow" data-testid={`tour-card-${t.id}`}>
                    <div className="h-40 bg-slate-100 dark:bg-[#1A2336] relative overflow-hidden">
                      {t.cover_image && <img src={t.cover_image} alt="" className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${t.status === "cancelled" ? "grayscale opacity-70" : ""}`} />}
                      <div className="absolute top-3 right-3 flex items-center gap-1.5">
                        {isScouting(t) && (
                          <span
                            data-testid={`tour-scouting-badge-${t.id}`}
                            className="text-[9px] mono uppercase tracking-widest px-2 py-1 rounded-full bg-fuchsia-100 text-fuchsia-700 border border-fuchsia-200 dark:bg-fuchsia-950/60 dark:text-fuchsia-300 dark:border-fuchsia-900"
                          >
                            Scouting
                          </span>
                        )}
                        <StatusBadge value={t.status} />
                      </div>
                    </div>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <Link to={`/tours/${t.id}`} className="font-bold text-lg hover:text-[#026FCB] block truncate">{t.name}</Link>
                          <div className="text-sm text-slate-500 mt-0.5">{t.destination}, {t.country}</div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(t)} title="Editar" data-testid={`tour-edit-${t.id}`}><Pencil size={14} /></Button>
                          <Button variant="ghost" size="sm" onClick={() => duplicate(t.id)} title="Duplicar" data-testid={`tour-duplicate-${t.id}`}><Copy size={14} /></Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteTour(t)} title="Eliminar" data-testid={`tour-delete-${t.id}`} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></Button>
                        </div>
                      </div>
                      <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                        <div><div className="text-slate-500 mono uppercase">Inicio</div><div className="font-medium mt-0.5">{fmtDate(t.start_date)}</div></div>
                        <div><div className="text-slate-500 mono uppercase">Fin</div><div className="font-medium mt-0.5">{fmtDate(t.end_date)}</div></div>
                        <div><div className="text-slate-500 mono uppercase">Precio</div><div className="font-medium mt-0.5">{money(t.price_per_participant, t.currency)}</div></div>
                      </div>
                      <div className="mt-3 text-xs text-slate-500 flex items-center justify-between">
                        <span>Cupo máx: <span className="font-medium text-slate-700 dark:text-slate-300">{t.max_clients}</span></span>
                        <span data-testid={`tour-pax-count-${t.id}`}>Participantes: <span className={`font-semibold ${((countByTour || {})[t.id] || 0) >= t.max_clients ? "text-red-600" : ((countByTour || {})[t.id] || 0) >= t.max_clients * 0.8 ? "text-amber-600" : "text-emerald-600"}`}>{(countByTour || {})[t.id] || 0}</span></span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )
          );
          return (
            <Tabs defaultValue="active">
              <TabsList className="flex-wrap h-auto">
                <TabsTrigger value="active" data-testid="tab-tours-active">Expediciones <span className="ml-2 text-xs mono opacity-70">{active.length}</span></TabsTrigger>
                <TabsTrigger value="scouting" data-testid="tab-tours-scouting">Scouting <span className="ml-2 text-xs mono opacity-70">{scouting.length}</span></TabsTrigger>
                <TabsTrigger value="finished" data-testid="tab-tours-finished">Finalizadas <span className="ml-2 text-xs mono opacity-70">{finished.length}</span></TabsTrigger>
                <TabsTrigger value="cancelled" data-testid="tab-tours-cancelled">Canceladas <span className="ml-2 text-xs mono opacity-70">{cancelled.length}</span></TabsTrigger>
              </TabsList>
              <TabsContent value="active" className="mt-4"><Grid list={active} empty="No hay expediciones activas. Crea una nueva o cambia de pestaña." /></TabsContent>
              <TabsContent value="scouting" className="mt-4"><Grid list={scouting} empty="Aún no hay tours de scouting. Crea uno marcando el tipo 'Scouting'." /></TabsContent>
              <TabsContent value="finished" className="mt-4"><Grid list={finished} empty="Aún no hay expediciones finalizadas." /></TabsContent>
              <TabsContent value="cancelled" className="mt-4"><Grid list={cancelled} empty="Ninguna expedición cancelada." /></TabsContent>
            </Tabs>
          );
        })()}
      </div>
    </div>
  );
}
